import React, { useState, useRef, useCallback } from 'react';
import { Icon } from '../utils/icons';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import api from '../services/api';

const ImageCropper = ({
    isOpen,
    onClose,
    onImageUploaded,
    aspectRatio = 1,
    title = "Crop Profile Picture",
    description = "Adjust the crop area to fit your profile picture perfectly",
    imageType = "profile" // profile, cover, logo, service
}) => {
    // Debug: Check if API is properly imported
    React.useEffect(() => {
        console.log('ImageCropper: API object check:', typeof api, api?.defaults?.baseURL);
    }, []);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5
    });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const imgRef = useRef(null);
    const fileInputRef = useRef(null);

    // Reset state when modal opens/closes
    React.useEffect(() => {
        if (!isOpen) {
            setSelectedFile(null);
            setImageSrc(null);
            setCrop({
                unit: '%',
                width: 90,
                height: 90,
                x: 5,
                y: 5
            });
            setCompletedCrop(null);
            setError('');
            setIsUploading(false);
        }
    }, [isOpen]);

    // File selection handler
    const handleFileSelect = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Please select a valid image file (JPEG, PNG, or WebP)');
            return;
        }

        // Validate file size (5MB max)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError('File size must be less than 5MB');
            return;
        }

        setError('');
        setSelectedFile(file);

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (event) => {
            setImageSrc(event.target.result);
        };
        reader.readAsDataURL(file);
    }, []);

    // Image load handler
    const onImageLoad = useCallback((e) => {
        const { width, height } = e.currentTarget;

        // Calculate crop to maintain aspect ratio
        const cropSize = Math.min(width, height) * 0.8;
        const x = (width - cropSize) / 2;
        const y = (height - cropSize) / 2;

        setCrop({
            unit: 'px',
            width: cropSize,
            height: cropSize,
            x: x,
            y: y
        });
    }, []);

    // Generate cropped image
    const getCroppedImg = useCallback((image, crop, fileName) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!crop || !image) {
            return null;
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // Set canvas size to desired output size (400x400 for profile pictures)
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;

        // Calculate crop dimensions
        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;
        const cropWidth = crop.width * scaleX;
        const cropHeight = crop.height * scaleY;

        // Draw cropped image to canvas
        ctx.drawImage(
            image,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            outputSize,
            outputSize
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    blob.name = fileName;
                    resolve(blob);
                } else {
                    resolve(null);
                }
            }, 'image/jpeg', 0.9);
        });
    }, []);

    // Handle upload
    const handleUpload = useCallback(async () => {
        if (!completedCrop || !imgRef.current || !selectedFile) {
            setError('Please select and crop an image first');
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            console.log('Starting image upload process...');
            console.log('Selected file:', selectedFile?.name, selectedFile?.size);
            console.log('Completed crop:', completedCrop);

            // Generate cropped image blob
            const croppedImageBlob = await getCroppedImg(
                imgRef.current,
                completedCrop,
                selectedFile.name
            );

            if (!croppedImageBlob) {
                throw new Error('Failed to generate cropped image');
            }

            console.log('Generated cropped blob:', croppedImageBlob?.size, 'bytes');

            // Create FormData for upload
            const formData = new FormData();

            // Choose endpoint and form field based on image type
            let uploadEndpoint;
            if (imageType === 'profile') {
                formData.append('profilePicture', croppedImageBlob, selectedFile.name);
                uploadEndpoint = '/upload/profile-picture';
            } else {
                formData.append('image', croppedImageBlob, selectedFile.name);
                formData.append('imageType', imageType);
                uploadEndpoint = '/upload/widget-image';
            }

            console.log('FormData created, sending upload request...');
            console.log('Upload URL:', uploadEndpoint);
            console.log('Image type:', imageType);

            // Upload to backend
            const response = await api.post(uploadEndpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            console.log('Upload response received:', response?.status, response?.data);

            if (response.data.success) {
                console.log('Upload successful, image URL:', response.data.data.url);
                // Call callback with uploaded image URL
                onImageUploaded(response.data.data.url);
                onClose();
            } else {
                throw new Error(response.data.message || 'Upload failed');
            }

        } catch (error) {
            console.error('Upload error details:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            console.error('Error message:', error.message);

            setError(
                error.response?.data?.message ||
                error.message ||
                'Failed to upload image. Please try again.'
            );
        } finally {
            setIsUploading(false);
        }
    }, [completedCrop, selectedFile, getCroppedImg, onImageUploaded, onClose]);

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const fakeEvent = { target: { files: files } };
            handleFileSelect(fakeEvent);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{description}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={isUploading}
                        >
                            <Icon name="close" size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 max-h-[80vh] overflow-y-auto">
                    {/* File Selection */}
                    {!imageSrc && (
                        <div
                            className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors cursor-pointer"
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            <div className="space-y-4">
                                <Icon name="upload" className="text-gray-400 mx-auto" size={48} />
                                <div>
                                    <p className="text-lg font-medium text-gray-900">
                                        Drop your image here, or click to browse
                                    </p>
                                    <p className="text-sm text-gray-500 mt-2">
                                        Support: JPEG, PNG, WebP (max 5MB)
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Image Cropper */}
                    {imageSrc && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <div className="max-w-2xl max-h-96 overflow-hidden">
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(c) => setCrop(c)}
                                        onComplete={(c) => setCompletedCrop(c)}
                                        aspect={aspectRatio}
                                        minWidth={100}
                                        minHeight={100}
                                        maxWidth={600}
                                        maxHeight={600}
                                        keepSelection={true}
                                        className="max-w-full"
                                    >
                                        <img
                                            ref={imgRef}
                                            alt="Crop preview"
                                            src={imageSrc}
                                            style={{ maxWidth: '100%', maxHeight: '400px' }}
                                            onLoad={onImageLoad}
                                        />
                                    </ReactCrop>
                                </div>
                            </div>

                            {/* Crop Instructions */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start space-x-3">
                                    <Icon name="info" className="text-blue-500 mt-0.5 flex-shrink-0" size={20} />
                                    <div>
                                        <p className="text-sm font-medium text-blue-800">How to crop:</p>
                                        <ul className="text-sm text-blue-700 mt-1 space-y-1">
                                            <li>• Drag the corners to resize the crop area</li>
                                            <li>• Drag inside the crop area to move it</li>
                                            <li>• The final image will be resized to 400x400 pixels</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                            <div className="flex items-center space-x-2">
                                <Icon name="error" className="text-red-500 flex-shrink-0" size={20} />
                                <p className="text-sm text-red-800">{error}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center">
                        {!imageSrc ? (
                            <>
                                <div></div>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => fileInputRef.current?.click()}
                                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Select Image
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <button
                                    onClick={() => {
                                        setImageSrc(null);
                                        setSelectedFile(null);
                                        setError('');
                                    }}
                                    className="text-gray-600 hover:text-gray-800 font-medium"
                                    disabled={isUploading}
                                >
                                    ← Choose Different Image
                                </button>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                                        disabled={isUploading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUpload}
                                        disabled={!completedCrop || isUploading}
                                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                    >
                                        {isUploading && (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        )}
                                        <span>{isUploading ? 'Uploading...' : 'Upload & Use'}</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
            />
        </div>
    );
};

export default ImageCropper;
